import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library, type IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCaretDown, faDatabase, faLayerGroup, faChartBar,
  faThLarge, faDownload, faList, faUsers, faCog, faPlusCircle,
  faArchive, faCircleUser, faReceipt, faSignOut, faTrash, faPlus, faFile, faArrowCircleRight,
  faArrowUpRightFromSquare, faUserPolice, faEyeSlash, faTable, faCaretRight, faGripVertical,
  faXmark, faEye, faDoNotEnter, faExclamationTriangle as faExclamationTriangleSolid, faBan,
  faAsterisk, faThumbtack, faFileCsv, faFolder as faFolderSolid, faLock as facLockSolid,
  faCircleExclamation, faFileCircleCheck, faMonitorWaveform as faMonitorWaveformSolid,
  faSquareInfo, faChevronDown as faChevronDownSolid,
  faClose, faTriangleExclamation, faPrint, faCheck, faArrowDown, faArrowUp,
  // Solid, to match the expand icon they sit beside on a graph's head row.
  faMagnifyingGlassMinus, faMagnifyingGlassPlus
} from '@fortawesome/pro-solid-svg-icons';
import {
  faDatabase as faDatabaseLight, faLayerGroup as faLayerGroupLight,
  faChartBar as faChartBarLight, faThLarge as faThLargeLight,
  faList as faListLight, faDownload as faDownloadLight,
  faUsers as faUsersLight, faCog as faCogLight,
  faPlusCircle as faPlusCircleLight, faArchive as faArchiveLight,
  faCircleUser as faCircleUserLight,
  faClipboard as faClipboardLight,
  faArrowDownArrowUp, faGear, faTrash as faTrashLight, faPenToSquare,
  faArrowTurnDownRight, faChevronDown, faFile as faFileLight, faFolder as faFolderLight,
  faChartSimple, faFileHalfDashed, faCalculatorSimple, faFolderCheck, faMailbox, faPeopleGroup,
  faNoteSticky as faNoteStickyLight,
  faBook, faCircleQuestion, faHouseBlank, faGridRound, faUser, faMobile,
  // The case tool rail draws its whole set from Light, so the two it was missing
  // are registered here rather than mixing weights across one strip of icons.
  faMonitorWaveform as faMonitorWaveformLight,
  faUserGroup as faUserGroupLight,
  faBell,
  // The "no results" state every table shares, from `Table.vue`.
  faFilterSlash,
  // The admin study's Child studies link in the sidebar.
  faSitemap as faSitemapLight
} from '@fortawesome/pro-light-svg-icons';
// Brand marks aren't part of the Pro icon sets, so the store logos come from the free package.
import { faApple, faAndroid, faGooglePlay } from '@fortawesome/free-brands-svg-icons';
import {
  faFileZipper, faDownload as faDownloadRegular, faEllipsisVertical, faCopy, faRotateExclamation,
  faFileExport, faFileImport, faHashtag, faText, faListUl, faSquareCaretDown, faCalendarDays, faLocationCrosshairs,
  faFileArrowUp, faQuoteLeft, faSquareSliders, faBarcodeRead, faFileSignature, faListTree, faPencil,
  faExclamationTriangle, faDotCircle, faCheckSquare, faCalendarAlt, faFolder, faFile as faFileRegular,
  faBarsProgress, faBoxArchive, faLock, faUnlock, faChevronLeft, faSignature, faEnvelope,
  faCircleCheck, faCircleXmark, faArrowRotateLeft, faArrowRotateRight, faFileLock, faWeightHanging, faFileCirclePlus, faChevronRight,
  faChartColumn, faChartPie, faChartLine, faTable as faTableRegular, faMonitorWaveform, faArrowRightArrowLeft,
  faCircleExclamation as faCircleExclamationRegular, faHeadset,
  faLeft, faRight, faClone, faFileCircleXmark,
  faNoteSticky, faClock, faClockRotateLeft, faPenToSquare as faPenToSquareRegular, faXmark as faXmarkRegular,
  faBars, faSliders,
  // The data section's search field. Regular, to sit at the weight of the clear beside it — the
  // two solid magnifiers above are the graph's zoom controls and are a different glyph.
  faMagnifyingGlass,
  // The pipeline cell's mark slot: a plus for an empty cell, a dash for a form the study does not
  // collect for this case. Bare glyphs, because the ring around them is drawn in CSS and a
  // `circle-plus` inside a ring would be two rings.
  faPlus as faPlusRegular, faMinus as faMinusRegular,
  // The pipeline's row header, which draws a finished case in the same mark slot. Regular, to sit
  // at the weight of the `far folder` it swaps places with — the Light copy above is the archive's
  // and is a different strip of icons.
  faFolderCheck as faFolderCheckRegular,
  // Each requirement a case has to meet before a hatched pipeline cell opens to it. A flow glyph
  // rather than the `far chevron-right` beside an outstanding field: those rows are buttons into a
  // form and these are not, and the two lists sit one above the other in the same card.
  faDiagramNext,
  // A form link: the strip above the entry, the button that follows it, and its row in Form Order.
  faCircleInfo, faArrowRight, faLink,
  // Delete in the selection bar, at the weight of Lock and Archive beside it.
  faTrash as faTrashRegular,
  // The Calculated chip beside an entry field's question (ENG-9765).
  faFunction,
  // A matrix's "← 2 more" button, which scrolls back to the columns hidden on its left (ENG-9907).
  faArrowLeft,
  // The scans panel's cards and live scan rows, and the scan detail's frame viewer (ENG-10008).
  faUser as faUserRegular, faImage, faImageSlash, faExpand, faChevronDown as faChevronDownRegular,
  faMagnifyingGlassPlus as faMagnifyingGlassPlusRegular,
  faMagnifyingGlassMinus as faMagnifyingGlassMinusRegular
} from '@fortawesome/pro-regular-svg-icons';
import { faPenToSquare as faPenToSquareDuo, faSparkles } from '@fortawesome/pro-duotone-svg-icons';

// Import the icon definitions separately to correctly specify their types
const icons: { [key: string]: IconDefinition } = {
  faCaretDown,
  faDatabase,
  faDatabaseLight,
  faLayerGroup,
  faLayerGroupLight,
  faChartBar,
  faChartBarLight,
  faThLarge,
  faThLargeLight,
  faDownload,
  faDownloadLight,
  faDownloadRegular,
  faList,
  faListLight,
  faUsers,
  faUsersLight,
  faCog,
  faCogLight,
  faPlusCircle,
  faPlusCircleLight,
  faArchive,
  faArchiveLight,
  faCircleUser,
  faCircleUserLight,
  faReceipt,
  faSignOut,
  faFileZipper,
  faTrash,
  faPlus,
  faEllipsisVertical,
  faClipboardLight,
  faArrowDownArrowUp,
  faFile,
  faArrowUpRightFromSquare,
  faCopy,
  faUserPolice,
  faRotateExclamation,
  faEyeSlash,
  faFileExport,
  faFileImport,
  faGear,
  faHashtag,
  faText,
  faListUl,
  faSquareCaretDown,
  faCalendarDays,
  faLocationCrosshairs,
  faFileArrowUp,
  faQuoteLeft,
  faSquareSliders,
  faBarcodeRead,
  faFileSignature,
  faListTree,
  faTable,
  faPencil,
  faCaretRight,
  faExclamationTriangle,
  faGripVertical,
  faXmark,
  faTrashLight,
  faDotCircle,
  faCheckSquare,
  faCalendarAlt,
  faEye,
  faArrowTurnDownRight,
  faDoNotEnter,
  faExclamationTriangleSolid,
  faBan,
  faAsterisk,
  faThumbtack,
  faFileCsv,
  faFolder,
  faChevronDown,
  faFileRegular,
  faBarsProgress,
  faFolderSolid,
  faFolderCheck,
  faFolderCheckRegular,
  faBoxArchive,
  faLock,
  faUnlock,
  facLockSolid,
  faChevronLeft,
  faFileLight,
  faFolderLight,
  faPenToSquare,
  faSignature,
  faEnvelope,
  faCircleCheck,
  faCircleXmark,
  faArrowRotateLeft,
  faFileLock,
  faArrowCircleRight,
  faWeightHanging,
  faCircleExclamation,
  faFileCircleCheck,
  faFileCirclePlus,
  faPlusRegular,
  faMinusRegular,
  faDiagramNext,
  faPenToSquareDuo: faPenToSquareDuo as IconDefinition,
  faSparkles: faSparkles as IconDefinition,
  faChevronRight,
  faChartSimple,
  faChartColumn,
  faChartPie,
  faChartLine,
  faTableRegular,
  faFileHalfDashed,
  faMonitorWaveform,
  faMonitorWaveformSolid,
  faSquareInfo,
  faArrowRightArrowLeft,
  faCalculatorSimple,
  faCircleExclamationRegular,
  faChevronDownSolid,
  faClose,
  faHeadset,
  faArrowRotateRight,
  faTriangleExclamation,
  faLeft,
  faRight,
  faClone,
  faPrint,
  faMailbox,
  faPeopleGroup,
  faCheck,
  faFileCircleXmark,
  faClockRotateLeft,
  faPenToSquareRegular,
  faNoteStickyLight,
  faArrowDown,
  faArrowUp,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
  faXmarkRegular,
  faBars,
  faSliders,
  faMagnifyingGlass,
  faBook,
  faCircleQuestion,
  faHouseBlank,
  faGridRound,
  faUser,
  faMobile,
  faMonitorWaveformLight,
  faUserGroupLight,
  faApple,
  faAndroid,
  faGooglePlay,
  faBell,
  faFilterSlash,
  faSitemapLight,
  faCircleInfo,
  faArrowRight,
  faLink,
  faTrashRegular,
  faFunction,
  faArrowLeft,
  faUserRegular,
  faImage,
  faImageSlash,
  faExpand,
  faChevronDownRegular,
  faMagnifyingGlassPlusRegular,
  faMagnifyingGlassMinusRegular
};

// Add the icons to the library
// standalone registrations after object definition
library.add(faNoteSticky, faClock);
Object.values(icons).forEach(icon => library.add(icon));

// Export the FontAwesomeIcon component
export { FontAwesomeIcon };
